/** PermissionsContext — per-org page-access gating (Phase 9 §Part1).
 *
 * Provides:
 *   canAccess(slug)  — true if the page is enabled for the caller's org (fail-open on error).
 *   slugForPath(pathname) — resolve a pathname to its canonical page slug (or null).
 *   isLoading        — true while the /api/me/permissions fetch is in-flight.
 *   isSuperAdmin     — true when the backend confirms this user is a platform super-admin.
 *
 * Behavior:
 *   - Locked slugs always return true from canAccess().
 *   - Unknown slugs always return true (fail-open for future/unregistered pages).
 *   - On fetch error, fail-open: canAccess() returns true for everything.
 *   - While loading: isLoading=true; PageGuard shows a spinner, not a block.
 */
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useMyPermissions } from "../api/permissions";
import type { PagePermission } from "../api/permissions";

// ── Route → slug mapping (canonical; must mirror the backend PAGE_REGISTRY) ───────────────────────

export const PAGE_SLUG_BY_ROUTE: Record<string, string> = {
  // Default pages
  "/welcome": "onboarding",
  "/setup": "onboarding",
  "/getting-started": "onboarding",
  "/configs": "edd-editor",
  "/merge": "edd-editor",
  "/formats": "edd-editor",
  "/templates": "edd-editor",
  "/new": "generate",
  "/generate": "generate",
  "/generate/jobs": "generate",
  // Optional pages
  "/marketplace": "marketplace",
  "/batch": "batch-generation",
  "/specs": "spec-registry",
  "/analytics": "analytics",
  "/admin/ai-usage": "analytics",
  "/rejections": "rejection-fix",
};

// Locked slugs (always accessible regardless of stored permissions).
const LOCKED_SLUGS = new Set(["onboarding", "edd-editor", "generate", "preview"]);

/** Resolve a pathname to its canonical page slug (exact, then prefix for parameterized routes). */
function resolveSlugForPath(pathname: string): string | null {
  if (PAGE_SLUG_BY_ROUTE[pathname] !== undefined) return PAGE_SLUG_BY_ROUTE[pathname];
  for (const [route, slug] of Object.entries(PAGE_SLUG_BY_ROUTE)) {
    if (pathname.startsWith(route + "/")) return slug;
  }
  return null;
}

// ── Context ───────────────────────────────────────────────────────────────────────────────────────

interface PermissionsState {
  canAccess: (slug: string) => boolean;
  slugForPath: (pathname: string) => string | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
}

const PermissionsContext = createContext<PermissionsState | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────────────────────────

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useMyPermissions();

  const permsBySlug = useMemo<Record<string, PagePermission>>(() => {
    if (!data) return {};
    return Object.fromEntries(data.permissions.map((p) => [p.slug, p]));
  }, [data]);

  const value = useMemo<PermissionsState>(
    () => ({
      isLoading,
      isSuperAdmin: data?.is_super_admin ?? false,

      canAccess(slug: string): boolean {
        // Fail-open: if fetch errored or is still loading, allow.
        if (isError) return true;
        // Locked slugs are always allowed.
        if (LOCKED_SLUGS.has(slug)) return true;
        // Unknown slug → fail-open.
        const perm = permsBySlug[slug];
        if (!perm) return true;
        return perm.enabled;
      },

      slugForPath: resolveSlugForPath,
    }),
    [isLoading, isError, data, permsBySlug],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────────────────────────

/** Safe fail-open default used when no PermissionsProvider is mounted (e.g. unit tests rendering
 *  <Layout> in isolation, or any consumer outside the provider). Allowing everything here is correct:
 *  page-access gating is a soft product-feature flag, never the security boundary — the server still
 *  authorizes every request. A missing provider must therefore never crash the nav. */
const FALLBACK_PERMISSIONS: PermissionsState = {
  canAccess: () => true,
  slugForPath: resolveSlugForPath,
  isLoading: false,
  isSuperAdmin: false,
};

export function usePermissions(): PermissionsState {
  return useContext(PermissionsContext) ?? FALLBACK_PERMISSIONS;
}
